import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Table, Input, Button, Space, Empty } from 'antd';
import { SearchOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { FilterDropdownProps, SorterResult, TableCurrentDataSource } from 'antd/es/table/interface';
import { getAllDeviceGroups, listDeviceGroups } from '@/api/devicegroupService';
import type { DeviceEntity } from '@/interface/responses/deviceResponse';

type Key = React.Key;

interface DevicesTableProps {
  loading?: boolean;
  data: DeviceEntity[];
  total: number;
  page: number;
  pageSize: number;
  onChangePage: (page: number, pageSize: number) => void;

  // Search props
  serialSearch?: string;
  onSearchSerial?: (q: string) => void;
  nameSearch?: string;
  onSearchName?: (q: string) => void;
  locationSearch?: string;
  onSearchLocation?: (q: string) => void;
  emailSearch?: string;
  onSearchEmail?: (q: string) => void;
  phoneSearch?: string;
  onSearchPhone?: (q: string) => void;

  // Filter props
  groupFilteredValues?: Key[];
  onFilterGroups?: (values: Key[]) => void;
  deviceTypeFilteredValues?: Key[];
  onFilterDeviceType?: (values: Key[]) => void;

  // Sort props
  onRequestSort?: (field: string, order: 'ascend' | 'descend' | null) => void;

  // Action props
  onViewDevice?: (device: DeviceEntity) => void;
  onEditDevice?: (device: DeviceEntity) => void;
  onDeleteDevice?: (device: DeviceEntity) => void;
}

const DevicesTable: React.FC<DevicesTableProps> = ({
  loading,
  data,
  total,
  page,
  pageSize,
  onChangePage,
  serialSearch,
  onSearchSerial,
  nameSearch,
  onSearchName,
  locationSearch,
  onSearchLocation,
  emailSearch,
  onSearchEmail,
  phoneSearch,
  onSearchPhone,
  groupFilteredValues,
  onFilterGroups,
  deviceTypeFilteredValues,
  onFilterDeviceType,
  onRequestSort,
  onViewDevice,
  onEditDevice,
  onDeleteDevice,
}) => {
  // Local input states for search dropdowns
  const [localSerialQuery, setLocalSerialQuery] = useState(serialSearch ?? '');
  const [localNameQuery, setLocalNameQuery] = useState(nameSearch ?? '');
  const [localLocationQuery, setLocalLocationQuery] = useState(locationSearch ?? '');
  const [localEmailQuery, setLocalEmailQuery] = useState(emailSearch ?? '');
  const [localPhoneQuery, setLocalPhoneQuery] = useState(phoneSearch ?? '');

  const serialInputRef = useRef<Input | null>(null);
  const nameInputRef = useRef<Input | null>(null);
  const locationInputRef = useRef<Input | null>(null);
  const emailInputRef = useRef<Input | null>(null);
  const phoneInputRef = useRef<Input | null>(null);

  // State for device groups from API
  const [deviceGroupOptions, setDeviceGroupOptions] = useState<{ text: string; value: Key }[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Fetch device groups from API
  useEffect(() => {
    const fetchDeviceGroups = async () => {
      setLoadingGroups(true);
      try {
        // ✅ getAllDeviceGroups returns DeviceGroup[] directly, not { data: DeviceGroup[] }
        const response = await getAllDeviceGroups();
        const options = response?.map(group => ({
          text: group.name,
          value: group.id
        })) || [];
        setDeviceGroupOptions(options);
      } catch (error) {
        console.error('Failed to fetch device groups:', error);
        setDeviceGroupOptions([]);
      } finally {
        setLoadingGroups(false);
      }
    };

    fetchDeviceGroups();
  }, []);

  // Update local states when props change
  useEffect(() => {
    setLocalSerialQuery(serialSearch ?? '');
  }, [serialSearch]);

  useEffect(() => {
    setLocalNameQuery(nameSearch ?? '');
  }, [nameSearch]);

  useEffect(() => {
    setLocalLocationQuery(locationSearch ?? '');
  }, [locationSearch]);

  useEffect(() => {
    setLocalEmailQuery(emailSearch ?? '');
  }, [emailSearch]);

  useEffect(() => {
    setLocalPhoneQuery(phoneSearch ?? '');
  }, [phoneSearch]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, (Key | boolean)[] | null>,
    sorter: SorterResult<DeviceEntity> | SorterResult<DeviceEntity>[],
    extra: TableCurrentDataSource<DeviceEntity>
  ) => {
    if (extra.action === 'paginate') {
      if (pagination.current && pagination.pageSize) {
        onChangePage?.(pagination.current, pagination.pageSize);
      }
      return;
    }
    if (extra.action === 'sort') {
      const s = Array.isArray(sorter) ? sorter[0] : sorter;
      const antdOrder = s?.order ?? null;
      const field = (s?.field as string) ?? (s?.columnKey as string) ?? '';
      
      // Map field names and order values
      const mapFieldToApiField = (frontendField: string): string => {
        const fieldMap: Record<string, string> = {
          'firmware_version': 'device.firmware_version',
          'phone_number': 'user.phone_number',
          'last_heartbeat': 'device.last_heartbeat',
        };
        return fieldMap[frontendField] || frontendField;
      };

      const mapOrderToApiOrder = (antdOrder: string | null): 'ASC' | 'DESC' | null => {
        if (!antdOrder) return null;
        const orderMap: Record<string, 'ASC' | 'DESC'> = {
          'ascend': 'ASC',
          'descend': 'DESC',
        };
        return orderMap[antdOrder] || null;
      };
      
      if (onRequestSort) {
        if (!antdOrder || !field) {
          onRequestSort('', null);
        } else {
          const apiField = mapFieldToApiField(field);
          const apiOrder = mapOrderToApiOrder(antdOrder);
          
          onRequestSort(apiField, apiOrder);
        }
      }
      return;
    }

    if (extra.action === 'filter') {
      
      // Handle group filters
      const groupVals = (filters?.groups || []) as Key[];
      if (onFilterGroups) {
        onFilterGroups(groupVals);
      }

      // Handle device type filters
      const deviceTypeVals = (filters?.device_type || []) as Key[];
      if (onFilterDeviceType) {
        onFilterDeviceType(deviceTypeVals);
      }
    }
  };

  // Update the search dropdown renderer to clear other searches
  const renderSearchDropdown = (
    placeholder: string,
    localValue: string,
    setLocalValue: (value: string) => void,
    onSearch: ((q: string) => void) | undefined,
    inputRef: React.RefObject<Input>,
    fieldName: string
  ) => {
    return (props: FilterDropdownProps) => {
      const { close, clearFilters } = props;
      
      const handleSearch = (searchValue: string) => {
        // Clear other local states when searching in this field
        if (searchValue.trim()) {
          if (fieldName !== 'device_serial') setLocalSerialQuery('');
          if (fieldName !== 'device_name') setLocalNameQuery('');
          if (fieldName !== 'location') setLocalLocationQuery('');
          if (fieldName !== 'email') setLocalEmailQuery('');
          if (fieldName !== 'phone') setLocalPhoneQuery('');
        }
        onSearch?.(searchValue.trim());
        close();
      };

      const handleReset = () => {
        setLocalValue('');
        onSearch?.('');
        clearFilters?.();
        close();
      };

      return (
        <div style={{ padding: 8, width: 260 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={inputRef}
            placeholder={placeholder}
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onPressEnter={() => handleSearch(localValue)}
            style={{ marginBottom: 8, display: 'block' }}
            allowClear
          />
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => handleSearch(localValue)}
              size="small"
            >
              Search
            </Button>
            <Button
              onClick={handleReset}
              size="small"
            >
              Reset
            </Button>
          </Space>
        </div>
      );
    };
  };

  // Update column definitions to use the correct sortBy keys
  const columns: ColumnsType<DeviceEntity> = useMemo(() => {
    return [
      {
        title: 'Device Name',
        key: 'device_name',
        dataIndex: 'device_name',
        filterDropdown: renderSearchDropdown(
          'Search device name',
          localNameQuery,
          setLocalNameQuery,
          onSearchName,
          nameInputRef,
          'device_name'
        ),
        filterIcon: <SearchOutlined />,
        filteredValue: nameSearch ? [nameSearch] : undefined,
        render: (_: any, rec: DeviceEntity) => (
          <span className="font-medium">
            {rec.device_name ?? `Device #${rec.id}`}
          </span>
        ),
      },
      {
        title: 'Device Serial Number',
        key: 'device_serial',
        dataIndex: 'device_serial',
        filterDropdown: renderSearchDropdown(
          'Search device serial number',
          localSerialQuery,
          setLocalSerialQuery,
          onSearchSerial,
          serialInputRef,
          'device_serial'
        ),
        filterIcon: <SearchOutlined />,
        filteredValue: serialSearch ? [serialSearch] : undefined,
        render: (_: any, rec: DeviceEntity) => (
          <span className="font-medium">
            {rec.device_serial ?? `Device #${rec.id}`}
          </span>
        ),
      },
      {
        title: 'Device Type',
        key: 'device_type',
        dataIndex: 'device_type',
        filters: [
          { text: 'STB', value: 'STB' },
          { text: 'Mobile', value: 'Mobile' },
        ],
        filteredValue: deviceTypeFilteredValues,
        filterMultiple: false,
        render: (_: any, rec: DeviceEntity) => (
          <span className="font-medium">
            {rec.device_type ?? '—'}
          </span>
        ),
      },
      {
        title: 'Device Group',
        key: 'groups',
        dataIndex: 'groups',
        filters: deviceGroupOptions,
        filteredValue: groupFilteredValues,
        filterMultiple: true,
        render: (_: any, rec: DeviceEntity) => {
          if (!rec.groups || rec.groups.length === 0) return '—';
          return rec.groups.map(g => g.name).join(', ');
        },
      },
      {
        title: 'Firmware Version',
        key: 'firmware_version',
        dataIndex: 'firmware_version',
        sorter: true,
        render: (value: string) => value || '—',
      },
      {
        title: 'Location',
        key: 'location',
        dataIndex: 'location',
        filterDropdown: renderSearchDropdown(
          'Search location',
          localLocationQuery,
          setLocalLocationQuery,
          onSearchLocation,
          locationInputRef,
          'location'
        ),
        filterIcon: <SearchOutlined />,
        filteredValue: locationSearch ? [locationSearch] : undefined,
        render: (value: string) => value || '—',
      },
      {
        title: 'Email',
        key: 'email',
        dataIndex: ['user', 'email'],
        filterDropdown: renderSearchDropdown(
          'Search email',
          localEmailQuery,
          setLocalEmailQuery,
          onSearchEmail,
          emailInputRef,
          'email'
        ),
        filterIcon: <SearchOutlined />,
        filteredValue: emailSearch ? [emailSearch] : undefined,
        render: (_: any, rec: DeviceEntity) => rec.user?.email ?? '—',
      },
      {
        title: 'Phone Number',
        key: 'phone_number',
        dataIndex: ['user', 'phone_number'],
        filterDropdown: renderSearchDropdown(
          'Search phone number',
          localPhoneQuery,
          setLocalPhoneQuery,
          onSearchPhone,
          phoneInputRef,
          'phone'
        ),
        filterIcon: <SearchOutlined />,
        filteredValue: phoneSearch ? [phoneSearch] : undefined,
        render: (_: any, rec: DeviceEntity) => rec.user?.phone_number ?? '—',
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_: any, rec: DeviceEntity) => (
          <Space size="small">
            <Button 
              type="text" 
              size="small" 
              icon={<EyeOutlined />} 
              onClick={() => onViewDevice?.(rec)} 
              title="View Device"
            />
            <Button 
              type="text" 
              size="small" 
              icon={<EditOutlined />} 
              onClick={() => onEditDevice?.(rec)} 
              title="Edit Device"
            />
            <Button 
              type="text" 
              size="small" 
              icon={<DeleteOutlined />} 
              onClick={() => onDeleteDevice?.(rec)} 
              danger 
              title="Delete Device"
            />
          </Space>
        ),
      },
    ];
  }, [
    deviceGroupOptions,
    groupFilteredValues,
    deviceTypeFilteredValues,
    localSerialQuery,
    localNameQuery,
    localLocationQuery,
    localEmailQuery,
    localPhoneQuery,
    serialSearch,
    nameSearch,
    locationSearch,
    emailSearch,
    phoneSearch,
    onSearchSerial,
    onSearchName,
    onSearchLocation,
    onSearchEmail,
    onSearchPhone,
    onViewDevice,
    onEditDevice,
    onDeleteDevice,
  ]);

  return (
    <Table
      rowKey={(r) => r.id}
      loading={loading || loadingGroups}
      columns={columns}
      dataSource={data}
      onChange={handleTableChange}
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} devices`,
      }}
      scroll={{ x: 1000 }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No devices found"
            style={{ padding: '20px 0' }}
          />
        ),
      }}
    />
  );
};

export default DevicesTable;
